import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/design-system/primitives';
import {
  type OverviewRow,
  TICKET_STATE_LABELS,
  useAdminRoster,
  useResolveTicket,
  useSupportOverview,
  useUpdateTicket,
} from '@/lib/api/ticketHooks';

import { TicketDrawer } from './TicketDrawer';
import {
  ClinicalAgentDrawer,
  type ClinicalAgentContext,
} from '../clinical-admin/ClinicalAgentDrawer';
import { PatientPreviewDrawer } from '../clinical-admin/PatientPreviewDrawer';

/**
 * Clinical-admin Reporting — lifecycle funnel over the in-house order flow
 * (replaces the CarePortals-only ClinicalOpsScreen queues). Metrics across
 * the funnel up top, then one section per stage in lifecycle order, sorted
 * stuck-longest-first. Each section shows the top 10; expanding reveals the
 * full list plus multi-select bulk actions (assign / mark fixed / resolve,
 * enabled for rows that have tickets — patient-owned stages like intake
 * mostly won't, by design).
 */

// boardStage maps a funnel section to the board's ?stage= filter so ops can
// jump from "what's stuck here" to "work those tickets" pre-filtered.
const STAGES: {
  key: keyof ReturnType<typeof emptyStages>;
  title: string;
  blurb: string;
  ageWarnDays: number; // row age highlighted red beyond this
  boardStage?: string;
}[] = [
  {
    key: 'needs_intake',
    title: 'Needs to complete intake',
    blurb:
      'Paid, but the medical intake is unfinished — nudge drips chase these; tickets appear once stuck past the threshold.',
    ageWarnDays: 3,
    boardStage: 'needs_intake',
  },
  {
    key: 'awaiting_consult',
    title: 'Awaiting consult',
    blurb: 'Sync-state patients who still need their visit before the script can be written.',
    ageWarnDays: 5,
  },
  {
    key: 'patient_blocked',
    title: 'Waiting on the patient',
    blurb: 'Photo ID missing, or the clinician asked the patient for more information.',
    ageWarnDays: 5,
  },
  {
    key: 'awaiting_rx',
    title: 'Awaiting Rx',
    blurb: 'Prereqs done — waiting on the prescriber to write the script (or on approval).',
    ageWarnDays: 1,
    boardStage: 'awaiting_rx',
  },
  {
    key: 'ops_hold',
    title: 'Held for ops',
    blurb:
      'Fulfillment holds (e.g. accumulated invoice cycles) and approved prescriptions whose pharmacy submission has not gone through. Nothing is owed by the patient.',
    ageWarnDays: 2,
    boardStage: 'ops_hold',
  },
  {
    key: 'at_pharmacy',
    title: 'Awaiting shipment',
    blurb: 'Submitted to the pharmacy, not yet shipped.',
    ageWarnDays: 2,
    boardStage: 'at_pharmacy',
  },
  {
    key: 'warnings',
    title: 'Pharmacy warnings',
    blurb: 'Unresolved partner-flagged exceptions — resolve via the order or the linked ticket.',
    ageWarnDays: 1,
    boardStage: 'warning',
  },
];

// Stages that group several derived statuses; rows name theirs.
const MIXED_STAGES = new Set<string>(['patient_blocked', 'ops_hold']);

function emptyStages() {
  return {
    needs_intake: [] as OverviewRow[],
    awaiting_consult: [] as OverviewRow[],
    patient_blocked: [] as OverviewRow[],
    awaiting_rx: [] as OverviewRow[],
    ops_hold: [] as OverviewRow[],
    at_pharmacy: [] as OverviewRow[],
    warnings: [] as OverviewRow[],
  };
}

export function TicketQueues({
  dateFrom,
  dateThrough,
}: {
  dateFrom: string;
  dateThrough: string;
}) {
  const { data, isLoading, error } = useSupportOverview(
    dateFrom,
    dateThrough,
    true,
  );
  const roster = useAdminRoster();
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [patientPreviewId, setPatientPreviewId] = useState<string | null>(null);
  const [agentContext, setAgentContext] = useState<ClinicalAgentContext | null>(null);

  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of roster.data?.admins ?? []) m[a.id] = a.display_name || a.email || a.id;
    return m;
  }, [roster.data]);

  if (isLoading) {
    return <div className="type-label-sm text-ink-muted py-40">Loading operational queues…</div>;
  }
  if (error || !data) {
    return <div className="type-label-sm text-danger py-40">Failed to load overview.</div>;
  }

  const m = data.metrics;
  const tiles: { label: string; value: number; danger?: boolean }[] = [
    { label: 'Needs intake', value: m.needs_intake },
    { label: 'Awaiting consult', value: m.awaiting_consult },
    { label: 'Waiting on patient', value: m.patient_blocked ?? 0 },
    { label: 'Awaiting Rx', value: m.awaiting_rx },
    { label: 'Held for ops', value: m.ops_hold ?? 0 },
    { label: 'Awaiting shipment', value: m.at_pharmacy },
    { label: 'Warnings', value: m.open_warnings, danger: m.open_warnings > 0 },
    { label: 'Overdue tickets', value: m.overdue_tickets, danger: m.overdue_tickets > 0 },
    { label: 'Shipped · 7d', value: m.shipped_last_7d },
  ];

  return (
    <div className="flex flex-col gap-20">
      {/* Funnel metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-10">
        {tiles.map((t) => (
          <div key={t.label} className="p-12 border border-line rounded-lg bg-raised">
            <p className="text-10 uppercase text-ink-muted">{t.label}</p>
            <p className={`type-heading-md mt-4 ${t.danger ? 'text-danger' : ''}`}>{t.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center">
        <span className="type-label-sm text-ink-muted">
          Order cohort {data.date_from} through {data.date_through} · sorted
          stuck-longest-first · ages match the sweep&apos;s ticket clocks
        </span>
        <Link to="/admin/support" className="type-label-sm text-accent hover:underline ml-auto">
          Open board (kanban / assign) →
        </Link>
      </div>

      {STAGES.map((s) => (
        <StageSection
          key={s.key}
          stage={s}
          rows={data.stages[s.key] ?? []}
          nameById={nameById}
          onOpenTicket={setOpenTicketId}
          onOpenPatient={setPatientPreviewId}
        />
      ))}

      <p className="type-label-sm text-ink-muted">
        Legacy CarePortals dashboard:{' '}
        <Link to="/admin/clinical-ops" className="text-accent hover:underline">
          /admin/clinical-ops
        </Link>{' '}
        (small remaining CP cohort only).
      </p>

      <TicketDrawer
        ticketId={openTicketId}
        onClose={() => setOpenTicketId(null)}
        onOpenPatient={setPatientPreviewId}
        onOpenAgent={setAgentContext}
      />
      <PatientPreviewDrawer
        patientId={patientPreviewId}
        onClose={() => setPatientPreviewId(null)}
        onOpenTicket={(ticketId) => {
          setPatientPreviewId(null);
          setOpenTicketId(ticketId);
        }}
        onOpenAgent={setAgentContext}
      />
      <ClinicalAgentDrawer
        open={Boolean(agentContext)}
        onClose={() => setAgentContext(null)}
        context={agentContext ?? {}}
      />
    </div>
  );
}

const TOP_N = 10;

function StageSection({
  stage,
  rows,
  nameById,
  onOpenTicket,
  onOpenPatient,
}: {
  stage: (typeof STAGES)[number];
  rows: OverviewRow[];
  nameById: Record<string, string>;
  onOpenTicket: (id: string) => void;
  onOpenPatient: (patientId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const update = useUpdateTicket();
  const resolve = useResolveTicket();
  const roster = useAdminRoster();
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkResolveReason, setBulkResolveReason] = useState('');

  const shown = expanded ? rows : rows.slice(0, TOP_N);
  const selectedTicketIds = rows
    .filter((r) => r.ticket_id && selected.has(r.order_id + (r.reason_code ?? '')))
    .map((r) => r.ticket_id as string);

  const rowKey = (r: OverviewRow) => r.order_id + (r.reason_code ?? '');
  const bulkClosureBlocked = rows.some((r) =>
    selected.has(rowKey(r)) && r.ticket_id &&
    (!r.ticket_source || r.ticket_source === 'missing_info'),
  );

  const toggle = (r: OverviewRow) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = rowKey(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const runBulk = (fn: (ticketId: string) => void) => {
    for (const id of selectedTicketIds) fn(id);
    setSelected(new Set());
  };

  return (
    <section className="border border-line rounded-lg bg-raised overflow-hidden">
      <div className="px-16 py-12 border-b border-line flex items-center gap-10">
        <Badge tone={rows.length > 0 ? 'warning' : 'success'} size="sm">
          {rows.length}
        </Badge>
        <div className="min-w-0">
          <h2 className="type-label-md font-semibold">{stage.title}</h2>
          <p className="type-label-sm text-ink-muted">{stage.blurb}</p>
        </div>
        <div className="ml-auto flex items-center gap-12 shrink-0">
          {stage.boardStage && rows.length > 0 && (
            <Link
              to={`/admin/support?stage=${stage.boardStage}`}
              className="type-label-sm text-accent hover:underline"
            >
              Work in board →
            </Link>
          )}
          {rows.length > TOP_N && (
            <button
              type="button"
              onClick={() => {
                setExpanded((v) => !v);
                setSelected(new Set());
              }}
              className="type-label-sm text-accent hover:underline"
            >
              {expanded ? 'Show top 10' : `Show all ${rows.length} + bulk actions`}
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar (expanded mode, rows with tickets) */}
      {expanded && (
        <div className="px-16 py-10 border-b border-line bg-sunken/40 flex items-center gap-10 flex-wrap">
          <span className="type-label-sm text-ink-muted">
            {selectedTicketIds.length} ticket{selectedTicketIds.length === 1 ? '' : 's'} selected
          </span>
          <select
            value={bulkAssignee}
            onChange={(e) => setBulkAssignee(e.target.value)}
            className="type-label-sm px-8 py-6 rounded border border-line bg-raised"
            aria-label="Bulk assignee"
          >
            <option value="">Assign to…</option>
            {(roster.data?.admins ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name || a.email || a.id}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!bulkAssignee || selectedTicketIds.length === 0}
            onClick={() => runBulk((id) => update.mutate({ id, assigned_to: bulkAssignee }))}
            className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Assign
          </button>
          <button
            type="button"
            disabled={selectedTicketIds.length === 0 || bulkClosureBlocked}
            onClick={() => runBulk((id) => update.mutate({ id, state: 'fixed' }))}
            className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Mark fixed
          </button>
          <input
            value={bulkResolveReason}
            onChange={(event) => setBulkResolveReason(event.target.value)}
            placeholder="Force-resolve reason"
            aria-label="Bulk force-resolve reason"
            className="type-label-sm px-8 py-6 rounded border border-line bg-raised min-w-[15rem]"
          />
          <button
            type="button"
            disabled={selectedTicketIds.length === 0 || !bulkResolveReason.trim() || bulkClosureBlocked}
            onClick={() =>
              runBulk((id) =>
                resolve.mutate({ id, note: bulkResolveReason.trim() }),
              )
            }
            className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Force resolve
          </button>
          <span className="type-label-sm text-ink-muted">
            {bulkClosureBlocked
              ? 'Open selected tickets individually; Missing Info requires verified evidence and Resolve & return to clinician.'
              : "(rows without a ticket can't be bulk-actioned)"}
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="px-16 py-14 type-label-sm text-ink-muted">Stage clear ✓</div>
      ) : (
        <table className="w-full text-left type-label-sm [&_th]:px-12 [&_th]:py-8 [&_td]:px-12 [&_td]:py-8">
          <thead className="text-10 uppercase text-ink-muted bg-sunken/50">
            <tr>
              {expanded && <th className="w-[2rem]" aria-label="Select" />}
              <th className="w-[5.5rem]">Stuck</th>
              <th>Order</th>
              {stage.key === 'warnings' && <th>Warning</th>}
              <th>Customer</th>
              <th className="w-[11rem]">Ticket</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const warn = (r.age_days ?? 0) > stage.ageWarnDays;
              return (
                <tr
                  key={rowKey(r)}
                  onClick={() => r.ticket_id && onOpenTicket(r.ticket_id)}
                  className={`border-t border-line ${
                    r.ticket_id ? 'hover:bg-sunken/40 cursor-pointer' : ''
                  }`}
                >
                  {expanded && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        disabled={!r.ticket_id}
                        checked={selected.has(rowKey(r))}
                        onChange={() => toggle(r)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  <td className={warn ? 'text-danger font-semibold' : 'text-ink-muted'}>
                    {r.age_days != null ? `${r.age_days}d` : '—'}
                  </td>
                  <td className="max-w-[20rem]">
                    <div className="line-clamp-1">
                      {r.order_number || r.order_id.slice(0, 8)} · {r.product_id ?? '?'}
                    </div>
                    <div className="font-mono text-10 text-ink-muted">
                      {r.partner_key ?? ''}
                      {r.pending_review ? ' · pending review' : ''}
                      {MIXED_STAGES.has(stage.key) && r.derived_status
                        ? ` · ${r.derived_status.replace(/_/g, ' ')}`
                        : ''}
                    </div>
                  </td>
                  {stage.key === 'warnings' && (
                    <td className="max-w-[16rem]">
                      <div className="line-clamp-1">{r.reason_description || r.reason_code}</div>
                    </td>
                  )}
                  <td>
                    {r.patient_id ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenPatient(r.patient_id as string);
                        }}
                        className="text-accent hover:underline"
                      >
                        {r.patient_name || r.patient_email || 'View customer'}
                      </button>
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {r.ticket_id ? (
                      <span>
                        {TICKET_STATE_LABELS[r.ticket_state ?? ''] ?? r.ticket_state}
                        {r.ticket_assigned_to
                          ? ` · ${nameById[r.ticket_assigned_to] ?? 'assigned'}`
                          : ''}
                      </span>
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
